package com.banyan.flightviewer.web.state

import com.banyan.flightviewer.data.AirportDatabase
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

/**
 * Deep-link handling. On boot, if the browser URL matches a known
 * share-pattern (`/flight/<hex>` or `/airport/<CODE>`), fulfil the
 * selection as soon as the app is ready.
 *
 * Flight flow (two-step):
 *  1. Ask the server for the plane's current lat/lon via
 *     `/api/aircraft/by-hex/<hex>` (backed by ADSB Exchange's icao
 *     endpoint). If it answers, push a map-focus request to pan the
 *     viewport there and trigger an immediate monitoring refresh.
 *  2. Wait (up to [WAIT_TIMEOUT_MS]) for the normal monitoring cycle
 *     to include the target hex in `appState.aircraftList`. When it
 *     arrives → `selectFlight(hex)`. The rest (photo, tracks, detail
 *     panel) is wired via the existing FlightTrackLoader + DetailPanel.
 *
 * Airport flow: airport data is bundled client-side in
 * [AirportDatabase], so no server call is needed. We just validate
 * the code and call `selectAirport`; MapView's existing
 * `selectedAirport` flow binding pans the map, AirportPanel's
 * observer renders the panel.
 *
 * Fallback paths:
 *  - Flight position lookup fails (ADSBX not configured on server,
 *    plane not broadcasting) → skip the pan; still poll the aircraft
 *    list in case the recipient is in the plane's area naturally.
 *  - Flight polling times out → clear the status message politely.
 *  - Unknown airport code → show a polite status message.
 *
 * Not on the roadmap yet:
 *  - `history.replaceState` on every selection so the URL always
 *    reflects the current view (copy-the-address-bar sharing).
 */
fun wireDeepLinkSelection(app: WebAppContainer) {
    val hex = parseFlightHexFromPath(window.location.pathname) ?: return

    app.appState.scope.launch {
        app.appState.setStatusMessage("Opening flight ${hex.uppercase()}\u2026")

        // Step 1: hex-lookup for position. The server returns null if the
        // plane isn't broadcasting or ADSBX isn't configured. Either way we
        // continue to the polling step; the pan is a best-effort shortcut.
        val position = app.serverClient.getAircraftByHex(hex)
        if (position != null) {
            app.uiState.requestMapFocus(position.lat, position.lon, zoom = 10)
            // Nudge the monitoring loop so it re-queries at the new viewport
            // immediately instead of waiting out the ~10s update interval.
            // Without this the user sees a pan, no planes for 10s, then
            // suddenly their flight appears — jarring.
            app.appState.requestImmediateRefresh()
        }

        // Step 2: wait for the hex to appear in the live list. Pre-check the
        // current value so a cache-hit (e.g. user navigates from another
        // view within the same session) selects instantly.
        val current = app.appState.aircraftList.value
        val arrived = if (current.any { it.hex.equals(hex, ignoreCase = true) }) {
            true
        } else {
            withTimeoutOrNull(WAIT_TIMEOUT_MS) {
                app.appState.aircraftList.first { list ->
                    list.any { it.hex.equals(hex, ignoreCase = true) }
                }
                true
            } ?: false
        }

        if (arrived) {
            // Use the exact case the data source used; `selectFlight` looks
            // up by exact match. ADSB is lowercase; some aggregators upper.
            val actualHex = app.appState.aircraftList.value
                .firstOrNull { it.hex.equals(hex, ignoreCase = true) }
                ?.hex
                ?: hex
            app.appState.selectFlight(actualHex)
            app.appState.setStatusMessage("")
        } else {
            // Polling timed out even after the map pan. The plane may have
            // landed or dropped below ADSB coverage. Clear politely.
            val msg = if (position != null) {
                "Flight ${hex.uppercase()} dropped off tracking. Try again soon."
            } else {
                "Flight ${hex.uppercase()} isn't currently broadcasting."
            }
            app.appState.setStatusMessage(msg)
            delay(10_000)
            if (app.appState.statusMessage.value == msg) {
                app.appState.setStatusMessage("")
            }
        }
    }
}

/**
 * Extracts the hex from a `/flight/<hex>` path. Returns null for anything
 * else.
 *
 * ICAO 24-bit hex identifiers are 6 hex chars; we accept any 6-char
 * alphanumeric-looking string and leave tighter validation to the
 * lookup (a bogus hex returns 404 from the server and we drop into the
 * "not currently broadcasting" fallback like any out-of-range real one).
 */
private fun parseFlightHexFromPath(path: String): String? {
    val trimmed = path.trim().trimEnd('/').ifEmpty { return null }
    val match = FLIGHT_PATH_REGEX.matchEntire(trimmed) ?: return null
    return match.groupValues[1].lowercase()
}

private val FLIGHT_PATH_REGEX = Regex("^/flight/([A-Za-z0-9]+)$")

// 30s covers a fresh monitoring cycle (default 10s) plus slack for slow
// networks; short enough that a broken deep link (plane already landed)
// gives a clear "not currently broadcasting" signal instead of a stuck
// spinner.
private const val WAIT_TIMEOUT_MS: Long = 30_000L

/**
 * Airport deep-link. Resolves the URL code to a canonical IATA form
 * (the database uses 3-letter IATA keys; US ICAO codes like KJFK are
 * translated by stripping the leading K, matching `AirportDatabase.getName`).
 * If the airport exists, selects it — MapView's existing
 * `selectedAirport` flow binding handles the pan; AirportPanel
 * observes and renders.
 */
fun wireDeepLinkAirportSelection(app: WebAppContainer) {
    val raw = parseAirportCodeFromPath(window.location.pathname) ?: return

    app.appState.scope.launch {
        val apt = AirportDatabase.findLocation(raw)
        if (apt != null) {
            app.appState.selectAirport(apt.code)
        } else {
            val msg = "Airport $raw not recognised."
            app.appState.setStatusMessage(msg)
            delay(10_000)
            if (app.appState.statusMessage.value == msg) {
                app.appState.setStatusMessage("")
            }
        }
    }
}

/**
 * Extracts the code from an `/airport/<CODE>` path. Returns uppercase
 * code or null. Accepts any 3- or 4-char alphanumeric so both IATA
 * (JFK) and ICAO (KJFK) forms work; existence validation happens
 * against [AirportDatabase] in the caller.
 */
private fun parseAirportCodeFromPath(path: String): String? {
    val trimmed = path.trim().trimEnd('/').ifEmpty { return null }
    val match = AIRPORT_PATH_REGEX.matchEntire(trimmed) ?: return null
    return match.groupValues[1].uppercase()
}

private val AIRPORT_PATH_REGEX = Regex("^/airport/([A-Za-z0-9]{3,4})$")

/**
 * Pre-seed [WebUiState.mapQueryArea] from the URL before monitoring
 * starts, so the very first fetch-cycle queries the deep-link target
 * instead of the user's home coords. Without this, the first cycle's
 * FR24 bulk-enrich call (1 `live/flight-positions/full` credit per
 * batch) is wasted on aircraft the user will never see.
 *
 * - `/airport/<CODE>`: synchronous lookup in [AirportDatabase].
 * - `/flight/<hex>`: one `/api/aircraft/by-hex/<hex>` round-trip
 *   (backed by ADSBX, far cheaper than FR24). Result is also used
 *   by [wireDeepLinkSelection] for the map pan, but the two calls
 *   aren't deduped -- the second one is cheap enough to not bother.
 * - No deep-link / unknown code: returns immediately; monitoring
 *   falls back to home coords as it always did.
 *
 * Must be awaited before `monitoring.start()` for the airport case
 * to have any effect; for the flight case the server round-trip
 * delays the first fetch slightly but saves the credit.
 */
suspend fun preSeedQueryAreaFromDeepLink(app: WebAppContainer) {
    val path = window.location.pathname

    parseAirportCodeFromPath(path)?.let { raw ->
        val apt = AirportDatabase.findLocation(raw) ?: return
        app.uiState.setMapQueryArea(MapQueryArea(apt.lat, apt.lon, DEEPLINK_SEED_RADIUS_KM))
        return
    }

    parseFlightHexFromPath(path)?.let { hex ->
        val pos = app.serverClient.getAircraftByHex(hex) ?: return
        app.uiState.setMapQueryArea(MapQueryArea(pos.lat, pos.lon, DEEPLINK_SEED_RADIUS_KM))
    }
}

// 50km comfortably covers an airport terminal area or a plane plus
// nearby traffic at zoom ~10, without over-querying. The map's own
// pushQueryArea will overwrite this as soon as the viewport pans.
private const val DEEPLINK_SEED_RADIUS_KM: Double = 50.0
